import LogoSvg from '@/assets/wntd-wordmark-orange.svg';

interface StyleProfileShareCardProps {
  imageUrl?: string;
  vibe?: string;
  bodyShape?: string;
  bodyShapeConfidence?: number;
  colorPalette?: string[];
  skinTone?: string;
  undertone?: string;
  estimatedSize?: string;
  styleDos?: string[];
  styleDonts?: string[];
  userName?: string;
}

const BODY_SHAPE_EMOJI: Record<string, string> = {
  'Hourglass': '⏳',
  'Pear': '🍐',
  'Apple': '🍎',
  'Rectangle': '▭',
  'Inverted Triangle': '🔻',
};

export function StyleProfileShareCard({
  imageUrl,
  vibe,
  bodyShape,
  bodyShapeConfidence,
  colorPalette = [],
  skinTone,
  undertone,
  estimatedSize,
  styleDos = [],
  styleDonts = [],
}: StyleProfileShareCardProps) {
  const displayColors = colorPalette.slice(0, 6);
  const shapeEmoji = bodyShape ? (BODY_SHAPE_EMOJI[bodyShape] || '✨') : '✨';
  const topDos = styleDos.slice(0, 2);
  const topDonts = styleDonts.slice(0, 2);

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: '#0a0a0a',
        padding: '48px 48px 40px',
      }}
    >
      {/* Hero — user photo with overlaid identity */}
      <div
        style={{
          height: 680,
          flexShrink: 0,
          borderRadius: 32,
          overflow: 'hidden',
          position: 'relative',
          backgroundColor: '#1a1a1a',
          marginBottom: 24,
        }}
      >
        {imageUrl ? (
          <img
            src={imageUrl}
            alt="Style profile"
            crossOrigin="anonymous"
            style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center top' }}
          />
        ) : (
          <div
            style={{
              width: '100%',
              height: '100%',
              background: 'linear-gradient(135deg, #FF5C4D 0%, #FF6F3B 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 80,
            }}
          >
            ✨
          </div>
        )}

        {/* Scrim */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(to top, rgba(0,0,0,0.88) 0%, rgba(0,0,0,0.0) 50%)',
          }}
        />

        {/* Top badge */}
        <div
          style={{
            position: 'absolute',
            top: 24,
            left: 24,
            padding: '8px 18px',
            borderRadius: 40,
            backgroundColor: 'rgba(0,0,0,0.55)',
            border: '1px solid rgba(255,255,255,0.12)',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <span style={{ fontSize: 18 }}>🧬</span>
          <span style={{ fontSize: 20, fontWeight: 600, color: 'white' }}>Style DNA</span>
        </div>

        {/* Bottom overlay — vibe + size */}
        <div
          style={{
            position: 'absolute',
            bottom: 24,
            left: 28,
            right: 28,
          }}
        >
          <p
            style={{
              fontSize: 44,
              fontWeight: 800,
              color: 'white',
              lineHeight: 1.1,
              marginBottom: 8,
              overflow: 'hidden',
              display: '-webkit-box',
              WebkitLineClamp: 1,
              WebkitBoxOrient: 'vertical',
            }}
          >
            {vibe || 'My Style'}
          </p>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            {estimatedSize && (
              <span
                style={{
                  fontSize: 18,
                  color: 'rgba(255,255,255,0.6)',
                  fontWeight: 500,
                  padding: '4px 14px',
                  borderRadius: 20,
                  backgroundColor: 'rgba(255,255,255,0.1)',
                  border: '1px solid rgba(255,255,255,0.12)',
                }}
              >
                {estimatedSize}
              </span>
            )}
            {bodyShape && (
              <span
                style={{
                  fontSize: 18,
                  color: '#FF6F3B',
                  fontWeight: 600,
                  padding: '4px 14px',
                  borderRadius: 20,
                  backgroundColor: 'rgba(255,111,59,0.15)',
                  border: '1px solid rgba(255,111,59,0.25)',
                }}
              >
                {shapeEmoji} {bodyShape}
                {bodyShapeConfidence ? ` · ${bodyShapeConfidence}%` : ''}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Color palette */}
      {displayColors.length > 0 && (
        <div
          style={{
            flexShrink: 0,
            marginBottom: 16,
            padding: '20px 24px',
            borderRadius: 24,
            backgroundColor: 'rgba(255,255,255,0.04)',
            border: '1px solid rgba(255,255,255,0.07)',
          }}
        >
          <p
            style={{
              fontSize: 16,
              color: 'rgba(255,255,255,0.4)',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: 2,
              marginBottom: 12,
            }}
          >
            Power Colors
          </p>
          <div style={{ display: 'flex', gap: 12 }}>
            {displayColors.map((color, i) => (
              <div
                key={i}
                style={{
                  flex: 1,
                  height: 56,
                  borderRadius: 14,
                  backgroundColor: color,
                  border: '1.5px solid rgba(255,255,255,0.08)',
                }}
              />
            ))}
          </div>
        </div>
      )}

      {/* Coloring info (skin tone + undertone) */}
      {(skinTone || undertone) && (
        <div
          style={{
            flexShrink: 0,
            marginBottom: 16,
            padding: '16px 24px',
            borderRadius: 24,
            backgroundColor: 'rgba(255,255,255,0.04)',
            border: '1px solid rgba(255,255,255,0.07)',
            display: 'flex',
            alignItems: 'center',
            gap: 14,
          }}
        >
          <span style={{ fontSize: 28 }}>🎨</span>
          <div>
            {skinTone && (
              <p style={{ fontSize: 22, fontWeight: 600, color: 'white', marginBottom: 2 }}>
                {skinTone}
              </p>
            )}
            {undertone && (
              <p style={{ fontSize: 18, color: 'rgba(255,255,255,0.45)' }}>
                {undertone} undertone
              </p>
            )}
          </div>
        </div>
      )}

      {/* Style rules */}
      {(topDos.length > 0 || topDonts.length > 0) && (
        <div style={{ flex: 1, display: 'flex', gap: 14, minHeight: 0 }}>
          {topDos.length > 0 && (
            <div
              style={{
                flex: 1,
                padding: '18px 20px',
                borderRadius: 24,
                backgroundColor: 'rgba(34,197,94,0.07)',
                border: '1px solid rgba(34,197,94,0.14)',
              }}
            >
              <p
                style={{
                  fontSize: 16,
                  fontWeight: 700,
                  color: '#22C55E',
                  marginBottom: 12,
                  textTransform: 'uppercase',
                  letterSpacing: 1.5,
                }}
              >
                ✓ Wear
              </p>
              {topDos.map((rule, i) => (
                <p
                  key={i}
                  style={{
                    fontSize: 19,
                    color: 'rgba(255,255,255,0.65)',
                    lineHeight: 1.4,
                    marginBottom: i < topDos.length - 1 ? 6 : 0,
                    overflow: 'hidden',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                  }}
                >
                  {rule}
                </p>
              ))}
            </div>
          )}

          {topDonts.length > 0 && (
            <div
              style={{
                flex: 1,
                padding: '18px 20px',
                borderRadius: 24,
                backgroundColor: 'rgba(239,68,68,0.07)',
                border: '1px solid rgba(239,68,68,0.14)',
              }}
            >
              <p
                style={{
                  fontSize: 16,
                  fontWeight: 700,
                  color: '#EF4444',
                  marginBottom: 12,
                  textTransform: 'uppercase',
                  letterSpacing: 1.5,
                }}
              >
                ✗ Avoid
              </p>
              {topDonts.map((rule, i) => (
                <p
                  key={i}
                  style={{
                    fontSize: 19,
                    color: 'rgba(255,255,255,0.65)',
                    lineHeight: 1.4,
                    marginBottom: i < topDonts.length - 1 ? 6 : 0,
                    overflow: 'hidden',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                  }}
                >
                  {rule}
                </p>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Footer */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          paddingTop: 28,
          flexShrink: 0,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <img src={LogoSvg} alt="WNTD" style={{ height: 36 }} />
          <span style={{ fontSize: 22, color: 'rgba(255,255,255,0.4)', fontWeight: 500 }}>
            Style DNA
          </span>
        </div>
        <span style={{ fontSize: 22, color: 'rgba(255,255,255,0.3)' }}>wntd.ai</span>
      </div>
    </div>
  );
}
